/* eslint-disable */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import {
  SUPPORTED_FORMATS,
  getOcrWorker,
  terminateOcrWorker,
  cropImageForBarcode,
  tryNativeBarcodeDetector,
  extractBarcode,
} from './scannerUtils';
import './BarcodeScanner.css';

/**
 * BarcodeScanner Component - scans EAN/CODE_128 barcodes via camera or file upload.
 */
export default function BarcodeScanner({ onScanSuccess, onClose }) {
  const scannerRef = useRef(null);
  const fileInputRef = useRef(null);
  const onScanSuccessRef = useRef(onScanSuccess);
  const onCloseRef = useRef(onClose);
  const mountedRef = useRef(true);
  const lastScannedRef = useRef('');
  const scanCooldownRef = useRef(false);
  const objectUrlsRef = useRef([]);

  const [isStarted, setIsStarted] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [scanError, setScanError] = useState('');
  const [hasTorch, setHasTorch] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [cameras, setCameras] = useState([]);
  const [camIndex, setCamIndex] = useState(0);
  const [scanFlash, setScanFlash] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);

  useEffect(() => {
    onScanSuccessRef.current = onScanSuccess;
  }, [onScanSuccess]);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Scan success handler
  const handleScanResult = useCallback((text) => {
    if (!text) return;
    if (mountedRef.current) setScanFlash(true);
    setTimeout(() => {
      if (mountedRef.current) setScanFlash(false);
    }, 300);
    try {
      navigator.vibrate && navigator.vibrate([60, 30, 60]);
    } catch (_) {
      // ignore vibration support failure
    }
    onScanSuccessRef.current && onScanSuccessRef.current(text);
  }, []);

  // Stop camera
  const stopScanner = useCallback(async () => {
    const s = scannerRef.current;
    if (!s) return;
    try {
      const state = s.getState();
      if (state === 2 || state === 3) await s.stop();
      await s.clear();
    } catch (_) {
      // ignore clear camera failure
    }
    scannerRef.current = null;
    if (mountedRef.current) setIsStarted(false);
  }, []);

  // Start camera
  const startCamera = useCallback(
    async (cameraConstraint) => {
      setScanError('');
      await stopScanner();

      await new Promise((r) => setTimeout(r, 80));
      if (!document.getElementById('bs-reader')) return;

      try {
        const scanner = new Html5Qrcode('bs-reader', {
          verbose: false,
          formatsToSupport: SUPPORTED_FORMATS,
        });
        scannerRef.current = scanner;

        const config = {
          fps: 10,
          qrbox: (w, h) => {
            const size = Math.floor(Math.min(w, h) * 0.85);
            return {
              width: size,
              height: Math.floor(size * 0.55),
            };
          },
          formatsToSupport: SUPPORTED_FORMATS,
        };

        // Set videoConstraints so facingMode/deviceId is declared ONLY ONCE.
        // Do NOT pass cameraConstraint as both first arg and inside videoConstraints
        // as it causes conflicting constraints on iOS Safari / Android Chrome.
        if (typeof cameraConstraint === 'object' && cameraConstraint.facingMode) {
          config.videoConstraints = {
            facingMode: cameraConstraint.facingMode,
            width: { ideal: 1280 },
            height: { ideal: 720 },
          };
        } else if (typeof cameraConstraint === 'object' && cameraConstraint.deviceId) {
          config.videoConstraints = {
            deviceId: cameraConstraint.deviceId,
            width: { ideal: 1280 },
            height: { ideal: 720 },
          };
        }

        await scanner.start(
          cameraConstraint,
          config,
          (text) => {
            if (scanCooldownRef.current || text === lastScannedRef.current) return;
            lastScannedRef.current = text;
            scanCooldownRef.current = true;
            setTimeout(() => {
              scanCooldownRef.current = false;
            }, 1500);

            handleScanResult(text);
          },
          () => {},
        );

        if (mountedRef.current) {
          setIsStarted(true);

          // Autofocus & optimal zoom
          setTimeout(async () => {
            if (scannerRef.current && mountedRef.current) {
              try {
                await scannerRef.current.applyVideoConstraints({
                  advanced: [{ focusMode: 'continuous' }, { zoom: 1.3 }],
                });
              } catch (e) {
                try {
                  await scannerRef.current.applyVideoConstraints({
                    advanced: [{ focusMode: 'continuous' }],
                  });
                } catch (_) {
                  // ignore autofocus setting failure
                }
              }
            }
          }, 1000);

          try {
            const track = scanner.getRunningTrackCapabilities();
            setHasTorch(!!(track && track.torch));
          } catch (_) {
            // ignore capability probe failure
          }
        }
      } catch (err) {
        console.error('Camera error:', err);
        if (mountedRef.current) {
          setScanError(
            'ไม่สามารถเปิดกล้องได้ค่ะ กรุณาอนุญาต Camera permission ในเบราว์เซอร์แล้วลองอีกครั้ง',
          );
        }
      }
    },
    [stopScanner, handleScanResult],
  );

  // Auto-start on mount
  useEffect(() => {
    mountedRef.current = true;
    startCamera({ facingMode: 'environment' });

    // Check for multiple cameras in background
    Html5Qrcode.getCameras()
      .then((devices) => {
        if (!mountedRef.current) return;
        if (devices && devices.length) {
          setCameras(devices);
          const backIdx = devices.findIndex(
            (d) =>
              d.label.toLowerCase().includes('back') ||
              d.label.toLowerCase().includes('environment') ||
              d.label.toLowerCase().includes('rear') ||
              d.label.toLowerCase().includes('หลัง'),
          );
          setCamIndex(backIdx >= 0 ? backIdx : 0);
        }
      })
      .catch((err) => {
        console.warn('Could not list cameras in background:', err);
      });

    const currentUrls = objectUrlsRef.current;

    return () => {
      mountedRef.current = false;
      stopScanner();
      terminateOcrWorker();
      // Prevent memory leaks
      currentUrls.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch (_) {
          // ignore object url revocation error
        }
      });
    };
  }, [startCamera, stopScanner]);

  // Toggle flash light
  const toggleTorch = useCallback(async () => {
    if (!scannerRef.current) return;
    try {
      const next = !torchOn;
      await scannerRef.current.applyVideoConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch (_) {
      // ignore torch toggle failure
    }
  }, [torchOn]);

  // Switch camera
  const switchCamera = useCallback(async () => {
    if (cameras.length < 2) return;
    const next = (camIndex + 1) % cameras.length;
    setCamIndex(next);
    await startCamera({ deviceId: { exact: cameras[next].id } });
  }, [cameras, camIndex, startCamera]);

  // File scanner fallback chain
  const scanFileWithFallback = useCallback(
    async (file) => {
      setScanError('');
      setOcrLoading(true);

      try {
        const tmp = new Html5Qrcode('bs-file-reader', {
          verbose: false,
          formatsToSupport: SUPPORTED_FORMATS,
        });
        try {
          const result = await tmp.scanFile(file, false);
          handleScanResult(result);
          setOcrLoading(false);
          return;
        } catch (_) {
          // try next fallback method
        } finally {
          await tmp.clear().catch(() => {});
        }
      } catch (_) {
        // try next fallback method
      }

      try {
        const tmp2 = new Html5Qrcode('bs-file-reader', {
          verbose: false,
          formatsToSupport: SUPPORTED_FORMATS,
        });
        try {
          const result2 = await tmp2.scanFileV2(file, false);
          handleScanResult(result2.decodedText);
          setOcrLoading(false);
          return;
        } catch (_) {
          // try native barcode detector fallback
        } finally {
          await tmp2.clear().catch(() => {});
        }
      } catch (_) {
        // try native barcode detector fallback
      }

      // Native BarcodeDetector fallback
      const native = await tryNativeBarcodeDetector(file);
      if (native) {
        setOcrLoading(false);
        handleScanResult(native);
        return;
      }

      // Tesseract OCR fallback (tries cropped image first, then full image)
      try {
        const worker = await getOcrWorker();

        // OCR on cropped image
        const croppedBlob = await cropImageForBarcode(file);
        const {
          data: { text: text1 },
        } = await worker.recognize(croppedBlob);
        const extracted1 = extractBarcode(text1);
        if (extracted1) {
          setOcrLoading(false);
          handleScanResult(extracted1);
          return;
        }

        // OCR on full image
        const {
          data: { text: text2 },
        } = await worker.recognize(file);
        const extracted2 = extractBarcode(text2);
        setOcrLoading(false);
        if (extracted2) {
          handleScanResult(extracted2);
        } else {
          if (mountedRef.current)
            setScanError('ไม่พบเลขบาร์โค้ดในภาพนี้ค่ะ กรุณาถ่ายให้เห็นตัวเลขบาร์โค้ดชัดๆ');
        }
      } catch (ocrErr) {
        setOcrLoading(false);
        if (mountedRef.current) setScanError('ไม่สามารถอ่านข้อมูลจากภาพได้ค่ะ');
      }
    },
    [handleScanResult],
  );

  // Scan from file upload
  const handleFileChange = useCallback(
    async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const url = URL.createObjectURL(file);
      objectUrlsRef.current.push(url);

      setGalleryImages((prev) => {
        const next = [{ url, file }, ...prev];
        if (next.length > 8) {
          next.slice(8).forEach((item) => URL.revokeObjectURL(item.url));
        }
        return next.slice(0, 8);
      });

      await scanFileWithFallback(file);
      e.target.value = '';
    },
    [scanFileWithFallback],
  );

  // Re-scan from gallery
  const handleScanGalleryImage = useCallback(
    async (item) => {
      await scanFileWithFallback(item.file);
    },
    [scanFileWithFallback],
  );

  const handleClose = useCallback(async () => {
    await stopScanner();
    onCloseRef.current && onCloseRef.current();
  }, [stopScanner]);

  return (
    <div className='bs-overlay'>
      {scanFlash && <div className='bs-scan-flash' />}

      <div className='bs-topbar'>
        <button className='bs-close-btn' onClick={handleClose} aria-label='ปิด'>
          <svg
            width='22'
            height='22'
            viewBox='0 0 24 24'
            fill='none'
            stroke='white'
            strokeWidth='2.5'
          >
            <line x1='18' y1='6' x2='6' y2='18' />
            <line x1='6' y1='6' x2='18' y2='18' />
          </svg>
        </button>
        <div className='bs-topbar-icons'>
          {hasTorch && (
            <button className='bs-icon-btn' title='ไฟฉาย' onClick={toggleTorch}>
              <svg
                width='22'
                height='22'
                viewBox='0 0 24 24'
                fill={torchOn ? '#FFD700' : 'none'}
                stroke={torchOn ? '#FFD700' : 'white'}
                strokeWidth='2'
              >
                <polygon points='13 2 3 14 12 14 11 22 21 10 12 10 13 2' />
              </svg>
            </button>
          )}
          {cameras.length > 1 && (
            <button className='bs-icon-btn' title='สลับกล้อง' onClick={switchCamera}>
              <svg
                width='22'
                height='22'
                viewBox='0 0 24 24'
                fill='none'
                stroke='white'
                strokeWidth='2'
              >
                <path d='M20 7h-4l-2-3H10L8 7H4a2 2 0 00-2 2v9a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z' />
                <circle cx='12' cy='13' r='3' />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className='bs-camera-area'>
        <div id='bs-reader' className='bs-reader'></div>

        {isStarted && <div className='bs-scanline' />}

        {isStarted && <div className='bs-scan-hint'>วางบาร์โค้ดในกรอบ — สแกนอัตโนมัติค่ะ</div>}

        {!isStarted && !scanError && (
          <div className='bs-camera-placeholder'>
            <div className='bs-viewfinder-corners'>
              <span className='bs-corner bs-tl'></span>
              <span className='bs-corner bs-tr'></span>
              <span className='bs-corner bs-bl'></span>
              <span className='bs-corner bs-br'></span>
            </div>
            <p className='bs-hint-text'>กำลังเปิดกล้อง...</p>
          </div>
        )}

        {!isStarted && scanError && (
          <div className='bs-camera-placeholder'>
            <div className='bs-viewfinder-corners'>
              <span className='bs-corner bs-tl'></span>
              <span className='bs-corner bs-tr'></span>
              <span className='bs-corner bs-bl'></span>
              <span className='bs-corner bs-br'></span>
            </div>
            <p className='bs-hint-text bs-hint-error'>{scanError}</p>
            <button
              className='bs-retry-btn'
              onClick={() => startCamera({ facingMode: 'environment' })}
            >
              ลองอีกครั้ง
            </button>
          </div>
        )}
      </div>

      {ocrLoading && (
        <div className='bs-ocr-bar'>
          <div className='bs-ocr-spinner' />
          <span>กำลังอ่านข้อมูลจากรูปภาพ...</span>
        </div>
      )}
      {!ocrLoading && isStarted && scanError && (
        <div className='bs-error-bar'>
          <span>{scanError}</span>
          <button onClick={() => setScanError('')}>✕</button>
        </div>
      )}

      <div className='bs-gallery-section'>
        <div className='bs-gallery-header'>
          <span className='bs-gallery-title'>อ่านจากรูปภาพ</span>
          <button
            className='bs-gallery-all'
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            เลือกรูป &rsaquo;
          </button>
        </div>

        <div className='bs-gallery-row'>
          <button
            className='bs-gallery-upload-tile'
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            aria-label='อัปโหลดภาพ'
          >
            <svg
              width='28'
              height='28'
              viewBox='0 0 24 24'
              fill='none'
              stroke='#aaa'
              strokeWidth='2'
            >
              <rect x='3' y='3' width='18' height='18' rx='2' />
              <circle cx='8.5' cy='8.5' r='1.5' />
              <polyline points='21 15 16 10 5 21' />
            </svg>
            <span>อัปโหลด</span>
          </button>

          {galleryImages.map((img, idx) => (
            <button
              key={idx}
              className='bs-gallery-thumb'
              onClick={() => handleScanGalleryImage(img)}
              title='สแกนภาพนี้'
            >
              <img src={img.url} alt={`gallery-${idx}`} />
              <span className='bs-gallery-scan-badge'>สแกน</span>
            </button>
          ))}
        </div>
      </div>

      <input
        ref={fileInputRef}
        type='file'
        accept='image/*'
        capture='environment'
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <div id='bs-file-reader' style={{ display: 'none' }}></div>
    </div>
  );
}
